import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useProducts } from '../../hooks/useProducts';
import { pueblosPuertoRico, categorias } from '../../mocks/pueblos';
import { marcasAutos } from '../../mocks/autos';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import MisVentas from './components/MisVentas';
import NotificacionesCampanita from './components/NotificacionesCampanita';
import ImageUploader from '../../components/feature/ImageUploader';
import RepDashboard from './components/RepDashboard';
import { supabase } from '../../lib/supabase';

type DashTab = 'articulos' | 'ventas' | 'perfil' | 'cobros' | 'representante';

const DashboardPage = () => {
  const { user, profile, loading, logout } = useAuth();
  const { createProduct, getProductsByVendedor, deleteProduct, loading: productsLoading } = useProducts();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<DashTab>('articulos');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [misProductos, setMisProductos] = useState<any[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [profileId, setProfileId] = useState<string>('');
  const [destacarModal, setDestacarModal] = useState<any>(null);
  const [editandoPerfil, setEditandoPerfil] = useState(false);
  const [perfilForm, setPerfilForm] = useState({ nombre_completo: '', pueblo: '', telefono: '', direccion: '' });
  const [guardandoPerfil, setGuardandoPerfil] = useState(false);
  const [perfilGuardado, setPerfilGuardado] = useState(false);
  const [procesandoDestaque, setProcesandoDestaque] = useState(false);
  const [showStripeRequiredModal, setShowStripeRequiredModal] = useState(false);
  
  // Stripe Connect state
  const [stripeConnectStatus, setStripeConnectStatus] = useState<any>(null);
  const [loadingStripeStatus, setLoadingStripeStatus] = useState(false);
  const [configurandoStripe, setConfigurandoStripe] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);

  // Estado del formulario
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    precio: '',
    categoria: '',
    estado: 'nuevo' as 'nuevo' | 'usado',
    pueblo: '',
    fotos: [] as string[],
    es_carga_pesada: false,
    // Moda
    talla: '',
    talla_calzado: '',
    color: '',
    medidas_ancho: '',
    medidas_largo: '',
    material: '',
    marca: '',
    es_calzado: false,
    // Autos
    marca_auto: '',
    modelo_auto: '',
    anio_auto: '',
    titulo_en_mano: false,
    garantia_dealer: false,
    // Subasta
    tipo_venta: 'precio_fijo' as 'precio_fijo' | 'subasta',
    precio_inicial: '',
    precio_reserva: '',
    fecha_fin_subasta: '',
    // Bienes Raíces
    tipo_propiedad: '',
    habitaciones: '',
    banos: '',
    estacionamientos: '',
    pies_cuadrados: '',
    cuerdas: '',
    direccion_propiedad: '',
    latitud: '',
    longitud: '',
    // Logística
    peso_libras: '',
    largo_in: '',
    ancho_in: '',
    alto_in: '',
    zip_origen: '',
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const modelosAutoDisponibles = formData.marca_auto ? (marcasAutos[formData.marca_auto] || []) : [];

  useEffect(() => {
    if (!loading) {
      if (!user) { navigate('/login'); return; }
      if (profile?.tipo_perfil === 'vendedor') {
        loadVendedorProducts();
        checkStripeConnectStatus();
      }
      if (user) {
        fetchProfileId();
      }
    }
  }, [user, profile, loading, navigate]);

  // Detectar retorno desde Stripe (stripe_onboard=success o refresh)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stripeOnboard = params.get('stripe_onboard');

    if (stripeOnboard === 'success' || stripeOnboard === 'refresh') {
      // Limpiar el parámetro de la URL sin recargar la página
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);

      // Ir a la pestaña de cobros y forzar re-check del estado
      setActiveTab('cobros');

      if (stripeOnboard === 'success') {
        // Esperar un momento para que Stripe procese y luego verificar
        setTimeout(() => {
          checkStripeConnectStatus();
        }, 1500);
      }
    }
  }, []);

  const fetchProfileId = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('profiles')
      .select('id')
      .eq('user_id', user.id)
      .maybeSingle();
    if (data) setProfileId(data.id);
  };

  const handleGuardarPerfil = async () => {
    if (!user) return;
    setGuardandoPerfil(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          nombre_completo: perfilForm.nombre_completo || null,
          pueblo: perfilForm.pueblo || null,
          telefono: perfilForm.telefono || null,
          direccion: perfilForm.direccion || null,
        })
        .eq('user_id', user.id);

      if (!error) {
        setPerfilGuardado(true);
        setEditandoPerfil(false);
        setTimeout(() => setPerfilGuardado(false), 3000);
      }
    } catch (err) {
      console.error('Error guardando perfil:', err);
    } finally {
      setGuardandoPerfil(false);
    }
  };

  const loadVendedorProducts = async () => {
    if (!user) return;
    const { data } = await getProductsByVendedor(user.id);
    if (data) setMisProductos(data);
  };

  const handleLogout = async () => {
    const result = await logout();
    if (result.success) navigate('/');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleFotosChange = (newFotos: string[]) => {
    setFormData(prev => ({ ...prev, fotos: newFotos }));
    if (formErrors.fotos) setFormErrors(prev => ({ ...prev, fotos: '' }));
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.titulo.trim()) errors.titulo = 'El título es requerido';
    if (!formData.descripcion.trim()) errors.descripcion = 'La descripción es requerida';
    if (!formData.precio || parseFloat(formData.precio) <= 0) errors.precio = 'El precio debe ser mayor a 0';
    if (!formData.categoria) errors.categoria = 'Selecciona una categoría';
    if (!formData.pueblo) errors.pueblo = 'Selecciona un pueblo';
    const fotosValidas = formData.fotos.filter(f => f.trim() !== '');
    if (fotosValidas.length === 0) errors.fotos = 'Sube al menos una foto del artículo';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    // Verificar Stripe Connect SOLO para artículos con envío (NO autos ni bienes raíces - son gratis y contacto directo)
    const requiereStripe = formData.categoria !== 'autos' && formData.categoria !== 'bienes_raices';
    const tieneStripeConnect = stripeConnectStatus?.has_account && stripeConnectStatus?.status === 'active';
    
    if (requiereStripe && !tieneStripeConnect) {
      setShowStripeRequiredModal(true);
      return;
    }

    const fotosValidas = formData.fotos.filter(f => f.trim() !== '');
    const productData: Record<string, any> = {
      titulo: formData.titulo.trim(),
      descripcion: formData.descripcion.trim(),
      precio: parseFloat(formData.precio),
      categoria: formData.categoria,
      estado: formData.estado,
      pueblo: formData.pueblo,
      fotos: fotosValidas,
      es_carga_pesada: formData.es_carga_pesada,
      peso_libras: formData.peso_libras ? parseFloat(formData.peso_libras) : null,
      largo_in: formData.largo_in ? parseFloat(formData.largo_in) : null,
      ancho_in: formData.ancho_in ? parseFloat(formData.ancho_in) : null,
      alto_in: formData.alto_in ? parseFloat(formData.alto_in) : null,
      zip_origen: formData.zip_origen.trim() || null,
    };

    if (formData.categoria === 'moda') {
      productData.talla = formData.talla || null;
      productData.talla_calzado = formData.es_calzado ? (formData.talla_calzado || null) : null;
      productData.color = formData.color.trim() || null;
      productData.medidas_ancho = formData.medidas_ancho.trim() || null;
      productData.medidas_largo = formData.medidas_largo.trim() || null;
      productData.material = formData.material.trim() || null;
      productData.marca = formData.marca.trim() || null;
    }

    if (formData.categoria === 'autos') {
      productData.marca_auto = formData.marca_auto || null;
      productData.modelo_auto = formData.modelo_auto || null;
      productData.anio_auto = formData.anio_auto ? parseInt(formData.anio_auto) : null;
      productData.titulo_en_mano = formData.titulo_en_mano;
      productData.garantia_dealer = formData.garantia_dealer;
    }

    if (formData.categoria === 'bienes_raices') {
      productData.tipo_propiedad = formData.tipo_propiedad || null;
      productData.habitaciones = formData.habitaciones ? parseInt(formData.habitaciones) : null;
      productData.banos = formData.banos ? parseInt(formData.banos) : null;
      productData.estacionamientos = formData.estacionamientos ? parseInt(formData.estacionamientos) : null;
      productData.pies_cuadrados = formData.pies_cuadrados ? parseInt(formData.pies_cuadrados) : null;
      productData.cuerdas = formData.cuerdas ? parseFloat(formData.cuerdas) : null;
      productData.direccion_propiedad = formData.direccion_propiedad.trim() || null;
      productData.latitud = formData.latitud ? parseFloat(formData.latitud) : null;
      productData.longitud = formData.longitud ? parseFloat(formData.longitud) : null;
    }

    // Subasta
    productData.tipo_venta = formData.tipo_venta;
    if (formData.tipo_venta === 'subasta') {
      productData.precio_inicial = formData.precio_inicial ? parseFloat(formData.precio_inicial) : parseFloat(formData.precio);
      productData.precio_reserva = formData.precio_reserva ? parseFloat(formData.precio_reserva) : null;
      productData.fecha_fin_subasta = formData.fecha_fin_subasta || null;
      productData.puja_actual = productData.precio_inicial;
      productData.estado_subasta = 'activa';
    }

    const { data, error } = await createProduct(productData);
    if (error) { setFormErrors({ submit: error }); return; }
    if (data) {
      setSubmitSuccess(true);
      setTimeout(() => {
        setShowPublishModal(false);
        setSubmitSuccess(false);
        resetForm();
        loadVendedorProducts();
      }, 2000);
    }
  };

  const resetForm = () => {
    setFormData({
      titulo: '', descripcion: '', precio: '', categoria: '', estado: 'nuevo',
      pueblo: '', fotos: [], es_carga_pesada: false,
      talla: '', talla_calzado: '', color: '', medidas_ancho: '', medidas_largo: '',
      material: '', marca: '', es_calzado: false,
      marca_auto: '', modelo_auto: '', anio_auto: '', titulo_en_mano: false, garantia_dealer: false,
      tipo_venta: 'precio_fijo', precio_inicial: '', precio_reserva: '', fecha_fin_subasta: '',
      tipo_propiedad: '', habitaciones: '', banos: '', estacionamientos: '',
      pies_cuadrados: '', cuerdas: '', direccion_propiedad: '', latitud: '', longitud: '',
      peso_libras: '',
      largo_in: '',
      ancho_in: '',
      alto_in: '',
      zip_origen: '',
    });
    setFormErrors({});
  };

  const handleDeleteProduct = async (productId: string) => {
    const { error } = await deleteProduct(productId);
    if (!error) { setDeleteConfirm(null); loadVendedorProducts(); }
  };

  const handleDestacarProducto = async () => {
    if (!destacarModal) return;
    setProcesandoDestaque(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-destaque-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            productoId: destacarModal.id,
            tituloProducto: destacarModal.titulo,
          }),
        }
      );

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'No se pudo iniciar el pago');
      }
    } catch (error) {
      console.error('Error al procesar destaque:', error);
    } finally {
      setProcesandoDestaque(false);
    }
  };

  const checkStripeConnectStatus = async () => {
    if (!user) return;
    setLoadingStripeStatus(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/stripe-connect-status`,
        {
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
          },
        }
      );
      const data = await response.json();
      setStripeConnectStatus(data);
    } catch (error) {
      console.error('Error checking Stripe Connect status:', error);
    } finally {
      setLoadingStripeStatus(false);
    }
  };

  const handleConfigurarCobro = async () => {
    if (!user) return;
    setConfigurandoStripe(true);
    setStripeError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        setStripeError('No hay sesión activa. Por favor recarga la página e intenta de nuevo.');
        setConfigurandoStripe(false);
        return;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/stripe-connect-onboard`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          signal: controller.signal,
        }
      );

      clearTimeout(timeoutId);

      const data = await response.json().catch(() => ({ error: `HTTP ${response.status} — respuesta inválida` }));

      if (!response.ok || data.error) {
        const msg = data.error || `Error HTTP ${response.status}`;
        const hint = data.hint || '';
        console.error('Error en stripe-connect-onboard:', data);
        setStripeError(hint ? `${msg}\n\n💡 ${hint}` : msg);
        setConfigurandoStripe(false);
        return;
      }

      if (data.url) {
        if (data.account_id) {
          localStorage.setItem('stripe_connect_account_id', data.account_id);
        }
        // Redirigir solo cuando tenemos URL válida
        window.location.href = data.url;
      } else {
        console.error('No se recibió URL de onboarding:', data);
        setStripeError('No se recibió el enlace de Stripe. Revisa los logs de la Edge Function en Supabase.');
        setConfigurandoStripe(false);
      }
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        setStripeError('Tiempo de espera agotado (30s). Verifica tu conexión e intenta de nuevo.');
      } else {
        setStripeError(error.message || 'Error de conexión inesperado.');
      }
      console.error('Error configurando Stripe Connect:', error);
      setConfigurandoStripe(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-gray-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) return null;

  const isVendedor = profile?.tipo_perfil === 'vendedor';
  const isComprador = profile?.tipo_perfil === 'comprador';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header */}
          <div className="bg-gray-900 rounded-2xl p-8 mb-8 text-white">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold mb-2">
                  ¡Bienvenido, {profile.nombre_completo || 'Vendedor'}!
                </h1>
                <p className="text-gray-400 text-sm flex items-center">
                  <i className="ri-map-pin-line mr-2"></i>
                  {profile.pueblo || 'Puerto Rico'}
                </p>
              </div>
              <div className="mt-4 md:mt-0 flex items-center gap-3">
                <NotificacionesCampanita userId={user.id} />
                <button
                  onClick={() => setShowLogoutConfirm(true)}
                  className="px-5 py-2.5 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  <i className="ri-logout-box-line mr-2"></i>
                  Cerrar Sesión
                </button>
              </div>
            </div>
          </div>

          {/* Stripe Connect Alert */}
          {stripeConnectStatus && !stripeConnectStatus.has_account && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-bank-card-line text-2xl text-amber-600"></i>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 mb-1">Configura tu cuenta de cobro</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Para recibir pagos de tus ventas con envío, necesitas configurar tu cuenta bancaria de forma segura a través de Stripe.
                  </p>
                  <p className="text-xs text-gray-500 mb-3 flex items-center gap-1.5">
                    <i className="ri-shield-check-line text-green-500"></i>
                    Te redirigiremos a Stripe para validar tu identidad. Es rápido y seguro.
                  </p>
                  <button
                    onClick={handleConfigurarCobro}
                    disabled={configurandoStripe}
                    className="px-5 py-2.5 bg-gray-900 text-white font-bold rounded-xl hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    {configurandoStripe ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Conectando con Stripe...
                      </span>
                    ) : (
                      <>
                        <i className="ri-secure-payment-line mr-2"></i>
                        Configurar cuenta de cobro
                      </>
                    )}
                  </button>
                  {stripeError && (
                    <div className="mt-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 whitespace-pre-line">
                      <i className="ri-error-warning-line mr-2"></i>
                      {stripeError}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {stripeConnectStatus?.has_account && stripeConnectStatus.status === 'restricted' && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-5 mb-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line text-2xl text-red-600"></i>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 mb-1">Acción requerida en tu cuenta de Stripe</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Stripe necesita información adicional para activar tu cuenta. Haz clic en el botón para completar el proceso directamente en Stripe.
                  </p>
                  <button
                    onClick={handleConfigurarCobro}
                    disabled={configurandoStripe}
                    className="px-5 py-2.5 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 text-sm"
                  >
                    {configurandoStripe ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Conectando...
                      </span>
                    ) : (
                      <><i className="ri-external-link-line mr-2"></i>Completar información en Stripe</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {stripeConnectStatus?.has_account && (stripeConnectStatus.status === 'pending' || stripeConnectStatus.status === 'pending_verification') && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-5 mb-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-2xl text-orange-600"></i>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 mb-1">Cuenta en verificación</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Tu cuenta de Stripe está siendo verificada. Esto puede tomar 1-2 días hábiles. Te notificaremos por email cuando esté activa.
                  </p>
                  <div className="flex items-center gap-2 text-sm text-orange-700">
                    <i className="ri-information-line"></i>
                    <span>Stripe está revisando tu información de identidad</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <i className="ri-shopping-bag-line text-xl text-gray-700"></i>
                </div>
                <span className="text-3xl font-bold text-gray-900">{misProductos.length}</span>
              </div>
              <h3 className="text-gray-500 text-sm font-medium">Artículos Publicados</h3>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <i className="ri-eye-line text-xl text-green-600"></i>
                </div>
                <span className="text-3xl font-bold text-gray-900">0</span>
              </div>
              <h3 className="text-gray-500 text-sm font-medium">Visitas Totales</h3>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <i className="ri-star-fill text-xl text-amber-500"></i>
                </div>
                <span className="text-3xl font-bold text-gray-900">
                  {(profile.reputacion ?? 5.0).toFixed(1)}
                </span>
              </div>
              <h3 className="text-gray-500 text-sm font-medium">Reputación</h3>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap items-center gap-1 mb-6 bg-white rounded-xl p-1 border border-gray-200 w-fit">
            {([
              { id: 'articulos', label: 'Mis Artículos', icon: 'ri-shopping-bag-line' },
              { id: 'ventas', label: 'Mis Ventas', icon: 'ri-money-dollar-circle-line' },
              { id: 'cobros', label: 'Cuenta de Cobro', icon: 'ri-bank-card-line' },
              { id: 'representante', label: 'Representante', icon: 'ri-user-star-line' },
              { id: 'perfil', label: 'Mi Perfil', icon: 'ri-user-line' },
            ] as { id: DashTab; label: string; icon: string }[]).map(t => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === t.id ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className={t.icon}></i>
                {t.label}
              </button>
            ))}
          </div>

          {/* Tab: Mis Artículos */}
          {activeTab === 'articulos' && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Mis Artículos</h2>
                <button
                  onClick={() => setShowPublishModal(true)}
                  className="px-5 py-2.5 bg-gray-900 text-white font-semibold rounded-xl hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  <i className="ri-add-line mr-2"></i>
                  Publicar Artículo
                </button>
              </div>

              {productsLoading ? (
                <div className="text-center py-12">
                  <div className="w-10 h-10 border-4 border-gray-900 border-t-transparent rounded-full animate-spin mx-auto"></div>
                </div>
              ) : misProductos.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-5">
                    <i className="ri-shopping-bag-3-line text-4xl text-gray-400"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">¡Comienza a Vender Hoy!</h3>
                  <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
                    Publica tu primer artículo y comienza a generar ingresos en IslaVende.
                  </p>
                  <button
                    onClick={() => setShowPublishModal(true)}
                    className="px-7 py-3 bg-gray-900 text-white font-bold rounded-xl hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-add-circle-line mr-2"></i>
                    Publicar Mi Primer Artículo
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {misProductos.map((producto) => (
                    <div key={producto.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-gray-300 transition-colors">
                      <div className="relative w-full h-44">
                        <img
                          src={producto.fotos?.[0] || 'https://public.readdy.ai/ai/img_res/01fc6575f3e65ede0c7bfccac59fae55.jpg'}
                          alt={producto.titulo}
                          className="w-full h-full object-cover object-top"
                        />
                        {producto.es_carga_pesada && (
                          <span className="absolute top-2 right-2 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-lg whitespace-nowrap">
                            Carga Pesada
                          </span>
                        )}
                        {/* Badge de Destacado */}
                        {producto.destacado && (
                          <span className="absolute top-2 left-2 bg-gradient-to-r from-amber-400 to-amber-500 text-white text-xs font-bold px-2 py-1 rounded-lg whitespace-nowrap flex items-center gap-1">
                            <i className="ri-vip-crown-line"></i>
                            Destacado
                          </span>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="font-bold text-gray-900 mb-1.5 line-clamp-2 text-sm">{producto.titulo}</h3>
                        <p className="text-xl font-bold text-gray-900 mb-2">${producto.precio?.toFixed(2)}</p>
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                          <span className="flex items-center gap-1">
                            <i className="ri-map-pin-line"></i>
                            {producto.pueblo}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            producto.estado === 'nuevo' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {producto.estado === 'nuevo' ? 'Nuevo' : 'Usado'}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/producto/${producto.id}`)}
                            className="flex-1 px-3 py-2 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap text-xs cursor-pointer"
                          >
                            Ver Detalles
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(producto.id)}
                            className="px-3 py-2 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-colors whitespace-nowrap cursor-pointer"
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                        
                        {/* Botón Destacar para Autos y Bienes Raíces */}
                        {(producto.categoria === 'autos' || producto.categoria === 'bienes_raices') && !producto.destacado && (
                          <button
                            onClick={() => setDestacarModal(producto)}
                            className="w-full mt-2 py-2 bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-white font-semibold rounded-lg transition-colors whitespace-nowrap text-xs cursor-pointer flex items-center justify-center gap-1"
                          >
                            <i className="ri-vip-crown-line"></i>
                            Destacar $9.99 / 7 días
                          </button>
                        )}
                        
                        {/* Info si ya está destacado */}
                        {producto.destacado && producto.destacado_hasta && (
                          <p className="text-[10px] text-amber-600 mt-2 text-center">
                            Destacado hasta: {new Date(producto.destacado_hasta).toLocaleDateString('es-PR')}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tab: Mis Ventas */}
          {activeTab === 'ventas' && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Mis Ventas</h2>
                <span className="text-xs text-gray-500">Logística Island Wide</span>
              </div>
              {profileId ? (
                <MisVentas userId={user.id} profileId={profileId} />
              ) : (
                <div className="text-center py-8 text-gray-500 text-sm">Cargando datos...</div>
              )}
            </div>
          )}

          {/* Tab: Cuenta de Cobro */}
          {activeTab === 'cobros' && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Cuenta de Cobro</h2>
                {stripeConnectStatus?.has_account && stripeConnectStatus.status === 'active' && (
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold flex items-center gap-1">
                    <i className="ri-checkbox-circle-fill"></i>
                    Activa
                  </span>
                )}
              </div>

              {loadingStripeStatus ? (
                <div className="text-center py-12">
                  <div className="w-10 h-10 border-4 border-gray-900 border-t-transparent rounded-full animate-spin mx-auto"></div>
                </div>
              ) : !stripeConnectStatus?.has_account ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-5">
                    <i className="ri-bank-card-line text-4xl text-amber-600"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Configura tu cuenta de cobro</h3>
                  <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
                    Para recibir pagos de tus ventas con envío, necesitas conectar tu cuenta bancaria de forma segura a través de Stripe.
                  </p>
                  <div className="bg-gray-50 rounded-xl p-5 mb-6 max-w-md mx-auto text-left">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <i className="ri-shield-check-line text-green-500"></i>
                      ¿Por qué Stripe?
                    </h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-green-500 mt-0.5"></i>
                        <span>Pagos seguros y protegidos</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-green-500 mt-0.5"></i>
                        <span>Depósitos directos a tu cuenta bancaria</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-green-500 mt-0.5"></i>
                        <span>Sin necesidad de compartir datos bancarios con compradores</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-green-500 mt-0.5"></i>
                        <span>Comisión de solo 15% por transacción</span>
                      </li>
                    </ul>
                  </div>
                  <p className="text-sm text-gray-500 mb-4 flex items-center justify-center gap-1.5">
                    <i className="ri-shield-check-line text-green-500"></i>
                    Te redirigiremos a Stripe para validar tu identidad. Es rápido y seguro.
                  </p>
                  <button
                    onClick={handleConfigurarCobro}
                    disabled={configurandoStripe}
                    className="px-7 py-3 bg-gray-900 text-white font-bold rounded-xl hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    {configurandoStripe ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Conectando con Stripe...
                      </span>
                    ) : (
                      <>
                        <i className="ri-secure-payment-line mr-2"></i>
                        Configurar cuenta de cobro
                      </>
                    )}
                  </button>
                  {stripeError && (
                    <div className="mt-4 bg-red-50 border border-red-200 rounded-xl px-5 py-4 text-sm text-red-700 text-left max-w-md mx-auto whitespace-pre-line">
                      <p className="font-bold mb-1 flex items-center gap-2">
                        <i className="ri-error-warning-line"></i>
                        Error al conectar con Stripe
                      </p>
                      <p>{stripeError}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Estado de la cuenta */}
                  <div className={`rounded-xl p-5 ${
                    stripeConnectStatus.status === 'active'
                      ? 'bg-green-50 border border-green-200'
                      : stripeConnectStatus.status === 'restricted'
                      ? 'bg-red-50 border border-red-200'
                      : 'bg-orange-50 border border-orange-200'
                  }`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        stripeConnectStatus.status === 'active' ? 'bg-green-100'
                        : stripeConnectStatus.status === 'restricted' ? 'bg-red-100'
                        : 'bg-orange-100'
                      }`}>
                        <i className={`text-2xl ${
                          stripeConnectStatus.status === 'active' ? 'ri-checkbox-circle-fill text-green-600'
                          : stripeConnectStatus.status === 'restricted' ? 'ri-error-warning-line text-red-600'
                          : 'ri-time-line text-orange-600'
                        }`}></i>
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">
                          {stripeConnectStatus.status === 'active' ? 'Cuenta activa'
                          : stripeConnectStatus.status === 'restricted' ? 'Información requerida'
                          : 'Cuenta en verificación'}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {stripeConnectStatus.status === 'active'
                            ? 'Puedes recibir pagos de tus ventas'
                            : stripeConnectStatus.status === 'restricted'
                            ? 'Stripe necesita datos adicionales para activar tu cuenta'
                            : 'Estamos verificando tu información'}
                        </p>
                      </div>
                    </div>

                    {stripeConnectStatus.status === 'restricted' && (
                      <div className="mt-3 pt-3 border-t border-red-200">
                        <p className="text-sm text-red-700 mb-3">
                          Haz clic abajo para ir a Stripe y completar la información que falta. Es rápido y solo toma unos minutos.
                        </p>
                        <button
                          onClick={handleConfigurarCobro}
                          disabled={configurandoStripe}
                          className="px-5 py-2.5 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 text-sm"
                        >
                          {configurandoStripe ? (
                            <span className="flex items-center gap-2">
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              Conectando...
                            </span>
                          ) : (
                            <><i className="ri-external-link-line mr-2"></i>Completar en Stripe</>
                          )}
                        </button>
                      </div>
                    )}

                    {stripeConnectStatus.status === 'active' && (
                      <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-green-200">
                        <div className="text-center">
                          <p className="text-xs text-gray-500 mb-1">Pagos habilitados</p>
                          <p className="font-semibold text-green-700 flex items-center justify-center gap-1">
                            <i className="ri-check-line"></i> Sí
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500 mb-1">Depósitos habilitados</p>
                          <p className="font-semibold text-green-700 flex items-center justify-center gap-1">
                            <i className="ri-check-line"></i> Sí
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Información de pagos */}
                  <div className="bg-gray-50 rounded-xl p-5">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <i className="ri-information-line text-gray-500"></i>
                      Información de pagos
                    </h4>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">ID de cuenta</span>
                        <span className="font-mono text-gray-900">{stripeConnectStatus.account_id?.slice(-8)}...</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Comisión de plataforma</span>
                        <span className="font-semibold text-gray-900">15%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tú recibes</span>
                        <span className="font-semibold text-green-700">85% de cada venta</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Frecuencia de depósitos</span>
                        <span className="font-semibold text-gray-900">Diaria</span>
                      </div>
                    </div>
                  </div>

                  {/* Botón para actualizar datos */}
                  {stripeConnectStatus.status === 'active' && (
                    <button
                      onClick={handleConfigurarCobro}
                      className="w-full py-3 border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-refresh-line mr-2"></i>
                      Actualizar información bancaria
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Tab: Representante */}
          {activeTab === 'representante' && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <i className="ri-user-star-line text-amber-500"></i>
                  Programa de Representantes
                </h2>
                <span className="px-3 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-xs font-semibold">
                  20% comisión
                </span>
              </div>
              {profileId ? (
                <RepDashboard
                  userId={user.id}
                  profileId={profileId}
                  nombreCompleto={profile.nombre_completo || ''}
                />
              ) : (
                <div className="text-center py-8 text-gray-500 text-sm">Cargando datos...</div>
              )}
            </div>
          )}

          {/* Tab: Mi Perfil */}
          {activeTab === 'perfil' && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Mi Perfil</h2>
                {!editandoPerfil ? (
                  <button
                    onClick={() => {
                      setPerfilForm({
                        nombre_completo: profile.nombre_completo || '',
                        pueblo: profile.pueblo || '',
                        telefono: profile.telefono || '',
                        direccion: profile.direccion || '',
                      });
                      setEditandoPerfil(true);
                    }}
                    className="px-4 py-2 text-gray-700 font-semibold hover:bg-gray-50 rounded-lg transition-colors whitespace-nowrap text-sm cursor-pointer border border-gray-200"
                  >
                    <i className="ri-edit-line mr-2"></i>
                    Editar Perfil
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditandoPerfil(false)}
                      className="px-4 py-2 text-gray-600 font-semibold hover:bg-gray-50 rounded-lg transition-colors whitespace-nowrap text-sm cursor-pointer border border-gray-200"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleGuardarPerfil}
                      disabled={guardandoPerfil}
                      className="px-4 py-2 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap text-sm cursor-pointer disabled:opacity-50"
                    >
                      {guardandoPerfil ? 'Guardando...' : 'Guardar Cambios'}
                    </button>
                  </div>
                )}
              </div>

              {perfilGuardado && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex items-center gap-2 text-green-700 text-sm">
                  <i className="ri-checkbox-circle-line"></i>
                  Perfil actualizado exitosamente
                </div>
              )}

              {editandoPerfil ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Nombre Completo</label>
                    <input
                      type="text"
                      value={perfilForm.nombre_completo}
                      onChange={(e) => setPerfilForm(prev => ({ ...prev, nombre_completo: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 text-sm"
                      placeholder="Tu nombre completo"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Pueblo</label>
                    <select
                      value={perfilForm.pueblo}
                      onChange={(e) => setPerfilForm(prev => ({ ...prev, pueblo: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 text-sm cursor-pointer"
                    >
                      <option value="">Selecciona tu pueblo</option>
                      {pueblosPuertoRico.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
                      Teléfono / WhatsApp <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={perfilForm.telefono}
                      onChange={(e) => setPerfilForm(prev => ({ ...prev, telefono: e.target.value.replace(/\D/g, '') }))}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 text-sm"
                      placeholder="7871234567"
                    />
                    <p className="text-xs text-gray-400 mt-1">Los compradores de Autos y Casas te contactarán aquí</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Correo Electrónico</label>
                    <input
                      type="email"
                      value={user.email || ''}
                      disabled
                      className="w-full px-4 py-2.5 border border-gray-100 rounded-lg text-sm bg-gray-50 text-gray-400 cursor-not-allowed"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Dirección</label>
                    <input
                      type="text"
                      value={perfilForm.direccion}
                      onChange={(e) => setPerfilForm(prev => ({ ...prev, direccion: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 text-sm"
                      placeholder="Calle, número, urbanización, código postal"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {[
                    { label: 'Nombre Completo', value: profile.nombre_completo },
                    { label: 'Pueblo', value: profile.pueblo },
                    {
                      label: 'Teléfono / WhatsApp',
                      value: profile.telefono,
                      extra: profile.telefono ? (
                        <a
                          href={`https://wa.me/1${profile.telefono}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 text-green-600 hover:text-green-700 text-xs font-semibold"
                        >
                          <i className="ri-whatsapp-line mr-1"></i>Probar
                        </a>
                      ) : null,
                    },
                    { label: 'Correo Electrónico', value: user.email },
                    { label: 'Tipo de Identificación', value: profile.tipo_identificacion },
                    { label: 'Número de Identificación', value: profile.numero_identificacion ? `****${profile.numero_identificacion.slice(-4)}` : null },
                  ].map((item) => (
                    <div key={item.label}>
                      <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">{item.label}</label>
                      <div className="flex items-center">
                        <p className="text-gray-900 font-medium text-sm">{item.value || 'No especificado'}</p>
                        {'extra' in item && item.extra}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal Publicar Artículo */}
      {showPublishModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Publicar Nuevo Artículo</h3>
              <button
                onClick={() => { setShowPublishModal(false); resetForm(); }}
                className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
              {submitSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-green-500"></i>
                  <p className="text-green-700 font-semibold">¡Artículo publicado exitosamente!</p>
                </div>
              )}
              {formErrors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                  <i className="ri-error-warning-fill text-2xl text-red-500"></i>
                  <p className="text-red-700 text-sm">{formErrors.submit}</p>
                </div>
              )}

              {/* Título */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Título del Artículo *</label>
                <input
                  type="text" name="titulo" value={formData.titulo} onChange={handleInputChange}
                  placeholder="Ej: iPhone 13 Pro Max 256GB"
                  className={`w-full px-4 py-2.5 border ${formErrors.titulo ? 'border-red-400' : 'border-gray-200'} rounded-xl focus:outline-none focus:border-gray-400 text-sm`}
                />
                {formErrors.titulo && <p className="text-red-500 text-xs mt-1">{formErrors.titulo}</p>}
              </div>

              {/* Descripción */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Descripción *</label>
                <textarea
                  name="descripcion" value={formData.descripcion} onChange={handleInputChange}
                  rows={3} placeholder="Describe tu artículo en detalle..."
                  className={`w-full px-4 py-2.5 border ${formErrors.descripcion ? 'border-red-400' : 'border-gray-200'} rounded-xl focus:outline-none focus:border-gray-400 text-sm resize-none`}
                />
                {formErrors.descripcion && <p className="text-red-500 text-xs mt-1">{formErrors.descripcion}</p>}
              </div>

              {/* Precio y Categoría */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Precio ($) *</label>
                  <input
                    type="number" name="precio" value={formData.precio} onChange={handleInputChange}
                    step="0.01" min="0" placeholder="0.00"
                    className={`w-full px-4 py-2.5 border ${formErrors.precio ? 'border-red-400' : 'border-gray-200'} rounded-xl focus:outline-none focus:border-gray-400 text-sm`}
                  />
                  {formErrors.precio && <p className="text-red-500 text-xs mt-1">{formErrors.precio}</p>}
                  {/* Cálculo de comisión neta */}
                  {formData.precio && parseFloat(formData.precio) > 0 && formData.categoria !== 'autos' && formData.categoria !== 'bienes_raices' && (
                    <div className="mt-2 bg-green-50 border border-green-200 rounded-lg p-2.5">
                      <p className="text-xs text-green-700 flex items-center gap-1.5">
                        <i className="ri-wallet-3-line text-green-600"></i>
                        <strong>Tú recibirás neto:</strong>
                        <span className="text-green-800 font-bold text-sm">${(parseFloat(formData.precio) * 0.85).toFixed(2)}</span>
                      </p>
                      <p className="text-[10px] text-green-600 mt-0.5 ml-5">Comisión de plataforma: 15% incluida</p>
                    </div>
                  )}
                  {(formData.categoria === 'autos' || formData.categoria === 'bienes_raices') && formData.precio && parseFloat(formData.precio) > 0 && (
                    <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-2.5">
                      <p className="text-xs text-blue-700 flex items-center gap-1.5">
                        <i className="ri-information-line text-blue-600"></i>
                        <strong>Publicación GRATIS</strong>
                      </p>
                      <p className="text-[10px] text-blue-600 mt-0.5 ml-5">Sin comisiones. Contacto directo con compradores.</p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Categoría *</label>
                  <select
                    name="categoria" value={formData.categoria} onChange={handleInputChange}
                    className={`w-full px-4 py-2.5 border ${formErrors.categoria ? 'border-red-400' : 'border-gray-200'} rounded-xl focus:outline-none focus:border-gray-400 text-sm cursor-pointer`}
                  >
                    <option value="">Selecciona una categoría</option>
                    {categorias.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                    ))}
                  </select>
                  {formErrors.categoria && <p className="text-red-500 text-xs mt-1">{formErrors.categoria}</p>}
                </div>
              </div>

              {/* Estado y Pueblo */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Condición *</label>
                  <select
                    name="estado" value={formData.estado} onChange={handleInputChange}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-gray-400 text-sm cursor-pointer"
                  >
                    <option value="nuevo">Nuevo</option>
                    <option value="usado">Usado</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Pueblo *</label>
                  <select
                    name="pueblo" value={formData.pueblo} onChange={handleInputChange}
                    className={`w-full px-4 py-2.5 border ${formErrors.pueblo ? 'border-red-400' : 'border-gray-200'} rounded-xl focus:outline-none focus:border-gray-400 text-sm cursor-pointer`}
                  >
                    <option value="">Selecciona un pueblo</option>
                    {pueblosPuertoRico.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                  {formErrors.pueblo && <p className="text-red-500 text-xs mt-1">{formErrors.pueblo}</p>}
                </div>
              </div>

              {/* ── CAMPOS EXCLUSIVOS DE AUTOS ── */}
              {formData.categoria === 'autos' && (
                <div className="border border-orange-200 rounded-xl p-5 bg-orange-50/40 space-y-4">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-7 h-7 flex items-center justify-center">
                      <i className="ri-car-line text-xl text-orange-500"></i>
                    </div>
                    <h4 className="font-bold text-gray-900 text-sm">Detalles del Vehículo</h4>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Marca *</label>
                      <select
                        name="marca_auto" value={formData.marca_auto}
                        onChange={(e) => { handleInputChange(e); setFormData(prev => ({ ...prev, modelo_auto: '' })); }}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-gray-400 text-sm cursor-pointer bg-white"
                      >
                        <option value="">Seleccionar marca</option>
                        {Object.keys(marcasAutos).map(m => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Modelo *</label>
                      <select
                        name="modelo_auto" value={formData.modelo_auto} onChange={handleInputChange}
                        disabled={!formData.marca_auto}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-gray-400 text-sm cursor-pointer bg-white disabled:opacity-50"
                      >
                        <option value="">Seleccionar modelo</option>
                        {modelosAutoDisponibles.map(m => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Año</label>
                      <input
                        type="number" name="anio_auto" value={formData.anio_auto} onChange={handleInputChange}
                        placeholder="Ej: 2022" min="1990" max="2025"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-gray-400 text-sm"
                      />
                    </div>
                  </div>

                  {/* Título en Mano y Garantía */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-xl cursor-pointer hover:border-gray-300 transition-colors">
                      <input
                        type="checkbox" name="titulo_en_mano" checked={formData.titulo_en_mano}
                        onChange={handleInputChange} className="w-4 h-4 accent-gray-900 cursor-pointer"
                      />
                      <div>
                        <p className="font-semibold text-gray-900 text-sm flex items-center gap-1.5">
                          <i className="ri-file-text-line text-green-600"></i>
                          Título en Mano
                        </p>
                        <p className="text-xs text-gray-500">El título del vehículo está disponible</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-xl cursor-pointer hover:border-gray-300 transition-colors">
                      <input
                        type="checkbox" name="garantia_dealer" checked={formData.garantia_dealer}
                        onChange={handleInputChange} className="w-4 h-4 accent-gray-900 cursor-pointer"
                      />
                      <div>
                        <p className="font-semibold text-gray-900 text-sm flex items-center gap-1.5">
                          <i className="ri-shield-check-line text-amber-600"></i>
                          Garantía de Dealer
                        </p>
                        <p className="text-xs text-gray-500">Incluye garantía del concesionario</p>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* ── CAMPOS EXCLUSIVOS DE MODA ── */}
              {formData.categoria === 'moda' && (
                <div className="border border-gray-200 rounded-xl p-5 bg-gray-50/40 space-y-4">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-7 h-7 flex items-center justify-center">
                      <i className="ri-shirt-line text-xl text-gray-600"></i>
                    </div>
                    <h4 className="font-bold text-gray-900 text-sm">Detalles de Moda y Ropa</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Size / Talla</label>
                      <select name="talla" value={formData.talla} onChange={handleInputChange}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-gray-400 text-sm bg-white cursor-pointer">
                        <option value="">Seleccionar talla</option>
                        {['XS', 'S', 'M', 'L', 'XL', 'XXL'].map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Color Principal</label>
                      <input type="text" name="color" value={formData.color} onChange={handleInputChange}
                        placeholder="Ej: Negro, Azul marino..."
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-gray-400 text-sm" />
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button type="button"
                      onClick={() => setFormData(prev => ({ ...prev, es_calzado: !prev.es_calzado, talla_calzado: '' }))}
                      className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 cursor-pointer ${formData.es_calzado ? 'bg-gray-900' : 'bg-gray-300'}`}>
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${formData.es_calzado ? 'translate-x-5' : 'translate-x-0.5'}`}></span>
                    </button>
                    <span className="text-sm font-semibold text-gray-700">¿Es calzado?</span>
                  </div>
                  {formData.es_calzado && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Talla de Calzado (USA)</label>
                      <select name="talla_calzado" value={formData.talla_calzado} onChange={handleInputChange}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-gray-400 text-sm bg-white cursor-pointer">
                        <option value="">Seleccionar talla</option>
                        {['5','5.5','6','6.5','7','7.5','8','8.5','9','9.5','10','10.5','11','11.5','12','12.5','13'].map(t => (
                          <option key={t} value={t}>US {t}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Material / Tela</label>
                      <input type="text" name="material" value={formData.material} onChange={handleInputChange}
                        placeholder="Ej: 100% Algodón..."
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-gray-400 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Marca / Brand</label>
                      <input type="text" name="marca" value={formData.marca} onChange={handleInputChange}
                        placeholder="Ej: Nike, Zara..."
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-gray-400 text-sm" />
                    </div>
                  </div>
                </div>
              )}

              {/* ── TIPO DE VENTA ── */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Tipo de Venta *</label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { val: 'precio_fijo', icon: 'ri-price-tag-3-line', title: 'Precio Fijo', desc: 'Venta directa' },
                    { val: 'subasta', icon: 'ri-auction-line', title: 'Subasta', desc: 'Los compradores pujan' },
                  ].map(opt => (
                    <button key={opt.val} type="button"
                      onClick={() => setFormData(prev => ({ ...prev, tipo_venta: opt.val as 'precio_fijo' | 'subasta' }))}
                      className={`p-3 border-2 rounded-xl transition-all text-left ${formData.tipo_venta === opt.val ? 'border-amber-500 bg-amber-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                      <div className="flex items-center gap-2 mb-0.5">
                        <i className={`${opt.icon} text-base ${formData.tipo_venta === opt.val ? 'text-amber-500' : 'text-gray-600'}`}></i>
                        <span className="font-bold text-gray-900 text-sm">{opt.title}</span>
                      </div>
                      <p className="text-xs text-gray-500">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {formData.tipo_venta === 'subasta' && (
                <div className="border border-amber-200 rounded-xl p-4 bg-amber-50/40 space-y-3">
                  <h4 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                    <i className="ri-auction-line text-amber-500"></i>Configuración de Subasta
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Precio Inicial ($)</label>
                      <input type="number" name="precio_inicial" value={formData.precio_inicial} onChange={handleInputChange}
                        placeholder="500" min="0" step="0.01"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-amber-400 text-sm bg-white" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Precio Reserva ($) <span className="text-gray-400">(opcional)</span></label>
                      <input type="number" name="precio_reserva" value={formData.precio_reserva} onChange={handleInputChange}
                        placeholder="Mínimo aceptable" min="0" step="0.01"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-amber-400 text-sm bg-white" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Fecha y Hora de Cierre</label>
                    <input type="datetime-local" name="fecha_fin_subasta" value={formData.fecha_fin_subasta} onChange={handleInputChange}
                      min={new Date().toISOString().slice(0, 16)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-amber-400 text-sm bg-white cursor-pointer" />
                  </div>
                </div>
              )}

              {formData.categoria === 'bienes_raices' && (
                <div className="border border-emerald-200 rounded-xl p-4 bg-emerald-50/40 space-y-3">
                  <h4 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                    <i className="ri-building-2-line text-emerald-600"></i>Detalles de la Propiedad
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { name: 'habitaciones', label: 'Habitaciones', placeholder: '3' },
                      { name: 'banos', label: 'Baños', placeholder: '2' },
                      { name: 'estacionamientos', label: 'Estacionamientos', placeholder: '1' },
                      { name: 'pies_cuadrados', label: 'Pies²', placeholder: '1200' },
                    ].map(f => (
                      <div key={f.name}>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">{f.label}</label>
                        <input type="number" name={f.name} value={(formData as any)[f.name]} onChange={handleInputChange}
                          placeholder={f.placeholder} min="0"
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-400 text-sm bg-white" />
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Tipo de Propiedad</label>
                      <select name="tipo_propiedad" value={formData.tipo_propiedad} onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-400 text-sm bg-white cursor-pointer">
                        <option value="">Seleccionar</option>
                        {['Casa','Apartamento','Solar','Finca','Local Comercial','Oficina'].map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Cuerdas (terreno)</label>
                      <input type="number" name="cuerdas" value={formData.cuerdas} onChange={handleInputChange}
                        placeholder="2.5" min="0" step="0.01"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-400 text-sm bg-white" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Dirección de la Propiedad</label>
                    <input type="text" name="direccion_propiedad" value={formData.direccion_propiedad} onChange={handleInputChange}
                      placeholder="Calle Principal #5, Urb. Villa del Rey, Caguas, PR"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-400 text-sm" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Latitud</label>
                      <input type="number" name="latitud" value={formData.latitud} onChange={handleInputChange}
                        placeholder="18.4655" step="0.0001"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-400 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Longitud</label>
                      <input type="number" name="longitud" value={formData.longitud} onChange={handleInputChange}
                        placeholder="-66.1057" step="0.0001"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-400 text-sm" />
                    </div>
                  </div>
                </div>
              )}

              {/* Peso y dimensiones del paquete */}
              {formData.categoria !== 'autos' && formData.categoria !== 'bienes_raices' && (
                <div className="border border-gray-200 rounded-xl p-4 bg-gray-50/40 space-y-4">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 flex items-center justify-center">
                      <i className="ri-box-3-line text-xl text-gray-500"></i>
                    </div>
                    <h4 className="font-bold text-gray-900 text-sm">
                      Medidas del Paquete
                      <span className="ml-1.5 text-xs font-normal text-gray-400">— Shippo calcula tarifas USPS en tiempo real</span>
                    </h4>
                  </div>

                  {/* Peso */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Peso (lbs) *</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        name="peso_libras"
                        value={formData.peso_libras}
                        onChange={handleInputChange}
                        step="0.1"
                        min="0.1"
                        max="70"
                        placeholder="Ej: 2.5"
                        className="w-36 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 text-sm bg-white"
                      />
                      <span className="text-sm text-gray-500">libras</span>
                    </div>
                  </div>

                  {/* Dimensiones */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Dimensiones (pulgadas) <span className="text-gray-400 font-normal">— recomendado para tarifas más precisas</span></label>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { name: 'largo_in', label: 'Largo', placeholder: '12' },
                        { name: 'ancho_in', label: 'Ancho', placeholder: '8' },
                        { name: 'alto_in', label: 'Alto', placeholder: '4' },
                      ].map(dim => (
                        <div key={dim.name}>
                          <label className="block text-[10px] text-gray-500 mb-1 font-semibold uppercase tracking-wide">{dim.label}</label>
                          <div className="relative">
                            <input
                              type="number"
                              name={dim.name}
                              value={(formData as any)[dim.name]}
                              onChange={handleInputChange}
                              step="0.5"
                              min="1"
                              placeholder={dim.placeholder}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 text-sm bg-white pr-7"
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">in</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* ZIP de origen del vendedor */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      ZIP Code de Origen <span className="text-red-500">*</span>
                      <span className="ml-1 text-gray-400 font-normal">— tu código postal para calcular tarifas USPS exactas</span>
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        name="zip_origen"
                        value={formData.zip_origen}
                        onChange={handleInputChange}
                        maxLength={5}
                        placeholder="Ej: 00901"
                        className="w-36 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 text-sm bg-white font-mono tracking-widest"
                      />
                      <span className="text-xs text-gray-400">Puerto Rico: 009XX · EE.UU.: 5 dígitos</span>
                    </div>
                    {formData.zip_origen && !/^\d{5}$/.test(formData.zip_origen) && (
                      <p className="text-amber-600 text-xs mt-1 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>
                        El ZIP debe tener exactamente 5 dígitos
                      </p>
                    )}
                  </div>

                  {/* Preview de tarifas estimadas */}
                  {formData.peso_libras && parseFloat(formData.peso_libras) > 0 && (
                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Tarifas USPS estimadas (Shippo calculará el precio exacto al comprar)</p>
                      <div className="flex gap-2 flex-wrap">
                        <span className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs text-gray-700">
                          <i className="ri-mail-send-line text-gray-500"></i>
                          <strong>Ground Advantage:</strong>
                          ~${parseFloat(formData.peso_libras) <= 1 ? '4.50' : parseFloat(formData.peso_libras) <= 2 ? '5.00' : parseFloat(formData.peso_libras) <= 5 ? '6.50' : parseFloat(formData.peso_libras) <= 10 ? '9.00' : '12.00+'}
                        </span>
                        <span className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs text-gray-700">
                          <i className="ri-rocket-line text-gray-500"></i>
                          <strong>Priority Mail:</strong>
                          ~${parseFloat(formData.peso_libras) <= 1 ? '8.50' : parseFloat(formData.peso_libras) <= 2 ? '9.50' : parseFloat(formData.peso_libras) <= 5 ? '12.00' : parseFloat(formData.peso_libras) <= 10 ? '16.00' : '20.00+'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Tipo de Envío */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Tipo de Envío Island Wide *</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { val: false, icon: 'ri-box-3-line', title: 'Paquete Regular', desc: 'Artículos pequeños y medianos', color: 'border-gray-900 bg-gray-50' },
                    { val: true, icon: 'ri-truck-line', title: 'Carga Pesada', desc: 'Enseres grandes y muebles', color: 'border-orange-500 bg-orange-50' },
                  ].map((opt) => (
                    <button key={String(opt.val)} type="button"
                      onClick={() => setFormData(prev => ({ ...prev, es_carga_pesada: opt.val }))}
                      className={`p-4 border-2 rounded-xl transition-all text-left ${
                        formData.es_carga_pesada === opt.val ? opt.color : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}>
                      <div className="flex items-center gap-2 mb-1">
                        <i className={`${opt.icon} text-lg ${opt.val ? 'text-orange-500' : 'text-gray-700'}`}></i>
                        <span className="font-bold text-gray-900 text-sm">{opt.title}</span>
                      </div>
                      <p className="text-xs text-gray-500">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Fotos */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Fotos del Artículo * <span className="text-gray-400 font-normal">(Máximo 5)</span>
                </label>
                <ImageUploader
                  fotos={formData.fotos.filter(f => f.trim() !== '')}
                  onChange={handleFotosChange}
                  maxFotos={5}
                  error={formErrors.fotos}
                />
              </div>
            </form>

            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button type="button" onClick={() => { setShowPublishModal(false); resetForm(); }}
                className="flex-1 px-5 py-3 border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer text-sm">
                Cancelar
              </button>
              <button onClick={handleSubmit} disabled={productsLoading || submitSuccess}
                className="flex-1 px-5 py-3 bg-gray-900 text-white font-semibold rounded-xl hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap text-sm cursor-pointer">
                {productsLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Publicando...
                  </span>
                ) : (
                  <><i className="ri-check-line mr-2"></i>Publicar Artículo</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Eliminar */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-delete-bin-line text-2xl text-red-500"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">¿Eliminar Artículo?</h3>
              <p className="text-gray-500 text-sm">Esta acción no se puede deshacer.</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer text-sm">
                Cancelar
              </button>
              <button onClick={() => handleDeleteProduct(deleteConfirm)}
                className="flex-1 px-4 py-3 bg-red-500 text-white font-semibold rounded-xl hover:bg-red-600 transition-colors whitespace-nowrap cursor-pointer text-sm">
                Sí, Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Logout */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-logout-box-line text-2xl text-red-500"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">¿Cerrar Sesión?</h3>
              <p className="text-gray-500 text-sm">¿Estás seguro que deseas cerrar tu sesión?</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer text-sm">
                Cancelar
              </button>
              <button onClick={handleLogout}
                className="flex-1 px-4 py-3 bg-red-500 text-white font-semibold rounded-xl hover:bg-red-600 transition-colors whitespace-nowrap cursor-pointer text-sm">
                Sí, Cerrar Sesión
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Destacar Producto */}
      {destacarModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-vip-crown-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Destaque Premium</h3>
              <p className="text-gray-500 text-sm">{destacarModal.titulo}</p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-gray-600">Duración</span>
                <span className="font-bold text-gray-900">7 días</span>
              </div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-gray-600">Posición</span>
                <span className="font-bold text-gray-900">Prioridad alta</span>
              </div>
              <div className="border-t border-amber-200 pt-3 flex items-center justify-between">
                <span className="text-sm font-bold text-gray-900">Total</span>
                <span className="text-2xl font-bold text-amber-600">$9.99</span>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-3 mb-6">
              <p className="text-xs text-gray-600 flex items-start gap-2">
                <i className="ri-check-line text-green-500 flex-shrink-0 mt-0.5"></i>
                Tu {destacarModal.categoria === 'autos' ? 'vehículo' : 'propiedad'} aparecerá en la parte superior de los resultados
              </p>
              <p className="text-xs text-gray-600 flex items-start gap-2 mt-2">
                <i className="ri-check-line text-green-500 flex-shrink-0 mt-0.5"></i>
                Badge dorado de "Destacado" en tu publicación
              </p>
              <p className="text-xs text-gray-600 flex items-start gap-2 mt-2">
                <i className="ri-check-line text-green-500 flex-shrink-0 mt-0.5"></i>
                Mayor visibilidad = más contactos potenciales
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setDestacarModal(null)}
                className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={handleDestacarProducto}
                disabled={procesandoDestaque}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-white font-semibold rounded-xl transition-colors whitespace-nowrap cursor-pointer text-sm disabled:opacity-50"
              >
                {procesandoDestaque ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Procesando...
                  </span>
                ) : (
                  'Pagar $9.99'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Stripe Connect Requerido */}
      {showStripeRequiredModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-bank-card-line text-3xl text-amber-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Configura tu cuenta de cobro</h3>
              <p className="text-gray-500 text-sm">
                Para publicar artículos con envío, necesitas configurar tu cuenta bancaria para recibir pagos de forma segura.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-gray-600 mb-2">
                <i className="ri-information-line text-amber-500 mr-1"></i>
                <strong>¿Por qué es necesario?</strong>
              </p>
              <ul className="text-sm text-gray-600 space-y-1 ml-5">
                <li>• Recibe pagos directamente en tu cuenta bancaria</li>
                <li>• Protección para ti y tus compradores</li>
                <li>• Sin necesidad de compartir datos bancarios</li>
              </ul>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowStripeRequiredModal(false)}
                className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  setShowStripeRequiredModal(false);
                  setActiveTab('cobros');
                }}
                className="flex-1 px-4 py-3 bg-gray-900 text-white font-semibold rounded-xl hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                <i className="ri-secure-payment-line mr-2"></i>
                Configurar ahora
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default DashboardPage;